import { getTraceContextFromScope } from '../currentScopes';
import type { Scope } from '../scope';
import type { TraceContext } from '../types/context';
import type { Event, EventHint } from '../types/event';
import type { Span } from '../types/span';
import { isPrimitive } from './is';
import { spanIsSampled, spanToTraceContext } from './spanUtils';

/**
 * The trace context of the span an error escaped, keyed by the error itself.
 *
 * We store the trace context rather than the span because that is the shape we apply to the event
 * later, and it snapshots the span as it failed instead of reading it back once it has ended.
 */
const escapedSpanTraceContexts = new WeakMap<object, TraceContext>();

/**
 * A `WeakMap` can only be keyed by an object, so an error thrown as a primitive (`throw 'boom'`)
 * has nothing we can hang the span on and is left unattributed.
 */
function toWeakMapKey(error: unknown): object | undefined {
  return isPrimitive(error) ? undefined : error;
}

/**
 * Remember which span an error escaped, so a later `captureException` can attribute the error to
 * the span that actually failed instead of whichever span happens to be active at capture time.
 *
 * The first span to see the error wins: as an error unwinds through nested spans, the innermost
 * one is the one that failed. Unsampled spans are skipped because they are never sent, so their
 * span id would point at a span that does not exist. Sampling rather than `isRecording()` is what
 * matters here: a span ended before the error escaped it, which is the norm for `startSpanManual`,
 * has stopped recording but is still sent.
 */
export function recordEscapedErrorSpan(error: unknown, span: Span): void {
  const key = toWeakMapKey(error);

  if (!key || !spanIsSampled(span) || escapedSpanTraceContexts.has(key)) {
    return;
  }

  escapedSpanTraceContexts.set(key, spanToTraceContext(span));
}

/**
 * Attribute an error event to the span the error escaped, if we recorded one.
 *
 * This only applies within the error's own trace. The stored span id is meaningless in another
 * trace, and the event's dynamic sampling context (which the envelope header is built from) is
 * derived from the root span of the trace the event is already on. Rewriting the trace id here
 * would leave the envelope header and body naming different traces.
 */
export function applyEscapedErrorSpanToEvent(event: Event, hint: EventHint, scope: Scope | undefined): void {
  const key = toWeakMapKey(hint.originalException);
  const traceContext = key && escapedSpanTraceContexts.get(key);

  if (!traceContext) {
    return;
  }

  // An error captured with no active span has no trace context yet: the scope's is merged in
  // further downstream. Resolve the trace the event will end up on the same way that merge does,
  // so the check below still knows which trace we are on.
  const eventTraceContext = event.contexts?.trace;
  const eventTraceId = eventTraceContext?.trace_id ?? (scope && getTraceContextFromScope(scope).trace_id);

  if (eventTraceId !== traceContext.trace_id) {
    return;
  }

  event.contexts = {
    ...event.contexts,
    trace: {
      ...eventTraceContext,
      ...traceContext,
    },
  };
}
